import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { patientsApi, doctorsApi, appointmentsApi } from '@/services/api';
import { toast } from 'sonner';
import { formatDate, getKolkataTodayString, utcToKolkataParts } from '@/utils/dateTime';
import { formatPhone } from '@/lib/utils';
import {
  ArrowLeft,
  Loader2,
  Search,
  Calendar as CalendarIcon,
  Clock,
  User,
  Stethoscope,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn, formatDoctorName, getDoctorInitials } from '@/lib/utils';
import { useClinic } from '@/contexts/ClinicContext';

const appointmentSchema = z.object({
  patientId: z.string().min(1, 'Please select a patient'),
  doctorId: z.string().min(1, 'Please select a doctor'),
  date: z.date({ required_error: 'Please select a date' }).refine(
    (date) => formatDate(date, 'YYYY-MM-DD') >= getKolkataTodayString(),
    { message: 'Cannot book appointments in the past' }
  ),
  time: z.string().min(1, 'Please select a time'),
  type: z.string().min(1, 'Please select appointment type'),
  duration: z.number().default(30),
  notes: z.string().optional(),
});

const timeSlots = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00',
];

const appointmentTypes = [
  { value: 'new-patient', label: 'New Patient' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'walk-in', label: 'Walk-in' },
];

const AppointmentFormPage = () => {
  const navigate = useNavigate();
  const { buildUrl } = useClinic();
  const [searchParams] = useSearchParams();
  const preselectedPatientId = searchParams.get('patientId');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [patientPopoverOpen, setPatientPopoverOpen] = useState(false);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      patientId: preselectedPatientId || '',
      doctorId: '',
      date: undefined,
      time: '',
      type: '',
      duration: 30,
      notes: '',
    },
  });

  const watchDate = watch('date');
  const watchDoctorId = watch('doctorId');
  const watchTime = watch('time');

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (watchDoctorId && watchDate) {
      loadDoctorSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchDoctorId, watchDate]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [patientsRes, doctorsRes] = await Promise.all([
        patientsApi.getAll({ limit: 100 }),
        doctorsApi.getAll({ status: 'active' }),
      ]);
      
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
      
      // If preselected patient
      if (preselectedPatientId) {
        const patient = patientsRes.data.find(p => p.id === preselectedPatientId);
        if (patient) {
          setSelectedPatient(patient);
          setValue('patientId', patient.id);
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadDoctorSchedule = async () => {
    try {
      const response = await doctorsApi.getSchedule(
        watchDoctorId,
        formatDate(watchDate, 'YYYY-MM-DD')
      );
      setBookedSlots(response.bookedSlots || []);
    } catch (error) {
      console.error('Failed to load doctor schedule:', error);
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setValue('patientId', patient.id);
    setPatientPopoverOpen(false);
  };

  const handleDoctorSelect = (doctorId) => {
    const doctor = doctors.find(d => d.id === doctorId);
    setSelectedDoctor(doctor);
    setValue('doctorId', doctorId);
    setValue('time', ''); // Reset time when doctor changes
  };

  const filteredPatients = patients.filter(p =>
    `${p.firstName} ${p.lastName}`.toLowerCase().includes(patientSearch.toLowerCase()) ||
    p.phone.includes(patientSearch)
  );

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      
      await appointmentsApi.create({
        patientId: data.patientId,
        doctorId: data.doctorId,
        clinicId: selectedDoctor?.assignedClinics?.[0],
        date: formatDate(data.date, 'YYYY-MM-DD'),
        time: data.time,
        duration: data.duration,
        type: data.type,
        notes: data.notes || '',
      });
      
      toast.success('Appointment scheduled successfully');
      navigate(buildUrl('appointments'));
    } catch (error) {
      toast.error(error.message || 'Failed to schedule appointment');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-3xl" data-testid="appointment-form-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="appointment-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(buildUrl('appointments'))}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">
            Schedule Appointment
          </h1>
          <p className="text-slate-500">
            Book a new appointment for a patient
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Patient Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-teal-600" />
              Select Patient
            </CardTitle>
            <CardDescription>Search and select the patient for this appointment</CardDescription>
          </CardHeader>
          <CardContent>
            {selectedPatient ? (
              <div className="flex items-center justify-between p-4 rounded-lg border border-teal-200 bg-teal-50">
                <div className="flex items-center gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-teal-100 text-teal-700">
                      {selectedPatient.firstName[0]}{selectedPatient.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900">
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </p>
                    <p className="text-sm text-slate-500 font-mono">
                      {selectedPatient.patientCode ? `${selectedPatient.patientCode} • ` : ''}{formatPhone(selectedPatient.phone)}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedPatient(null);
                    setValue('patientId', '');
                  }}
                  data-testid="change-patient-btn"
                >
                  Change
                </Button>
              </div>
            ) : (
              <Popover open={patientPopoverOpen} onOpenChange={setPatientPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-start h-11 text-slate-500"
                    data-testid="select-patient-btn"
                  >
                    <Search className="mr-2 h-4 w-4" />
                    Search patient by name or phone...
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search patient..."
                      value={patientSearch}
                      onValueChange={setPatientSearch}
                    />
                    <CommandList>
                      <CommandEmpty>No patient found.</CommandEmpty>
                      <CommandGroup>
                        {filteredPatients.slice(0, 10).map((patient) => (
                          <CommandItem
                            key={patient.id}
                            value={`${patient.firstName} ${patient.lastName}`}
                            onSelect={() => handlePatientSelect(patient)}
                            data-testid={`patient-option-${patient.id}`}
                          >
                            <Avatar className="h-8 w-8 mr-3">
                              <AvatarFallback className="bg-slate-100 text-slate-600 text-xs">
                                {patient.firstName[0]}{patient.lastName[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{patient.firstName} {patient.lastName}</p>
                              <p className="text-sm text-slate-500 font-mono">
                                {patient.patientCode ? `${patient.patientCode} • ` : ''}{formatPhone(patient.phone)}
                              </p>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
            {errors.patientId && (
              <p className="text-sm text-red-500 mt-2">{errors.patientId.message}</p>
            )}
          </CardContent>
        </Card>

        {/* Doctor Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Stethoscope className="h-5 w-5 text-teal-600" />
              Select Doctor
            </CardTitle>
            <CardDescription>Choose the doctor for this appointment</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={watch('doctorId')} onValueChange={handleDoctorSelect}>
              <SelectTrigger data-testid="select-doctor">
                <SelectValue placeholder="Select a doctor" />
              </SelectTrigger>
              <SelectContent>
                {doctors.map(doctor => (
                  <SelectItem key={doctor.id} value={doctor.id}>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={doctor.avatar} />
                        <AvatarFallback className="bg-teal-100 text-teal-700 text-xs">
                          {getDoctorInitials(doctor)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p>{formatDoctorName(doctor)}</p>
                        <p className="text-sm text-slate-500">{doctor.specialization}</p>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.doctorId && (
              <p className="text-sm text-red-500 mt-2">{errors.doctorId.message}</p>
            )}
          </CardContent>
        </Card>

        {/* Date & Time Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CalendarIcon className="h-5 w-5 text-teal-600" />
              Date & Time
            </CardTitle>
            <CardDescription>Select appointment date and time slot</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date Picker */}
              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !watchDate && "text-slate-500"
                      )}
                      data-testid="date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {watchDate ? formatDate(watchDate, 'FULL') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={watchDate}
                      onSelect={(date) => {
                        setValue('date', date);
                        setValue('time', ''); // Reset time when date changes
                      }}
                      disabled={(date) => formatDate(date, 'YYYY-MM-DD') < getKolkataTodayString()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {errors.date && (
                  <p className="text-sm text-red-500">{errors.date.message}</p>
                )}
              </div>

              {/* Appointment Type */}
              <div className="space-y-2">
                <Label>Appointment Type</Label>
                <Select value={watch('type')} onValueChange={(v) => setValue('type', v)}>
                  <SelectTrigger data-testid="select-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {appointmentTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.type && (
                  <p className="text-sm text-red-500">{errors.type.message}</p>
                )}
              </div>
            </div>

            {/* Time Slots */}
            {watchDate && watchDoctorId && (
              <div className="space-y-2">
                <Label>Available Time Slots</Label>
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                  {timeSlots.map((slot) => {
                    const isBooked = bookedSlots.includes(slot);
                    const isSelected = watchTime === slot;
                    
                    return (
                      <Button
                        key={slot}
                        type="button"
                        variant={isSelected ? 'default' : 'outline'}
                        size="sm"
                        disabled={isBooked}
                        onClick={() => setValue('time', slot)}
                        className={cn(
                          isSelected && 'bg-teal-600 hover:bg-teal-700',
                          isBooked && 'opacity-50 cursor-not-allowed'
                        )}
                        data-testid={`time-slot-${slot}`}
                      >
                        {slot}
                      </Button>
                    );
                  })}
                </div>
                {errors.time && (
                  <p className="text-sm text-red-500">{errors.time.message}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Additional Notes</CardTitle>
            <CardDescription>Any special instructions or notes for this appointment</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Enter any notes or special instructions..."
              {...watch('notes') !== undefined && { value: watch('notes') }}
              onChange={(e) => setValue('notes', e.target.value)}
              data-testid="notes-input"
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(buildUrl('appointments'))}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-teal-600 hover:bg-teal-700"
            disabled={saving}
            data-testid="submit-btn"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Schedule Appointment
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AppointmentFormPage;
